import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { getFeedPage, getQuestionnaireFeedPage } from 'src/service/engageFrontendGateway';
import { FeedItem, FeedSort, FeedSortDirection } from 'types/feeds';

/** Whose block this is: a page's content block, or a questionnaire's (survey end screens). */
export interface MixedListOwner {
  kind: 'page' | 'questionnaire';
  id: string;
}

interface UseMixedListOptions {
  locale: string;
  organization?: string;
  contentId: string;
  version?: string;
  sort?: FeedSort;
  sortDirection?: FeedSortDirection;
  pageSize: number;
  preview?: boolean;
}

/**
 * Mixed-list data: page 1 is seeded from the SSR bake but ALWAYS refetched on mount so
 * titles/statuses are live despite the CDN-cached page payload; further pages accumulate
 * client-side behind load-more.
 */
export const useMixedList = (
  initialFeed: FeedItem[] | undefined,
  owner: MixedListOwner,
  options: UseMixedListOptions,
) => {
  const { contentId, locale, version, sort, sortDirection, pageSize } = options;
  const [extraPages, setExtraPages] = useState<FeedItem[][]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const queryOptions = useMemo(
    () => ({
      locale,
      organization: options.organization,
      contentId,
      ...(version ? { version } : {}),
      ...(sort ? { sort, sortDirection } : {}),
      ...(options.preview ? { preview: true } : {}),
    }),
    [locale, options.organization, contentId, version, sort, sortDirection, options.preview],
  );

  const fetchPage = (pageNumber: number) =>
    owner.kind === 'questionnaire'
      ? getQuestionnaireFeedPage(
          owner.id,
          { ...queryOptions, organization: queryOptions.organization ?? '' },
          pageNumber,
          pageSize,
        )
      : getFeedPage(owner.id, 'mixed_list', queryOptions, pageNumber, pageSize);

  const { data, isLoading, error } = useQuery({
    queryKey: ['mixedList', owner.kind, owner.id, contentId, locale, version, sort, sortDirection, pageSize],
    queryFn: () => fetchPage(1),
    // SSR bake gives instant paint; totalPages arrives with the live refetch.
    initialData: initialFeed ? { items: initialFeed, totalPages: 1 } : undefined,
    refetchOnMount: 'always',
  });

  const loadedPages = 1 + extraPages.length;
  const totalPages = data?.totalPages ?? 0;
  const hasMore = loadedPages < totalPages;

  const loadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const next = await fetchPage(loadedPages + 1);
      setExtraPages((pages) => [...pages, next.items]);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const feed = useMemo<FeedItem[]>(() => [...(data?.items || []), ...extraPages.flat()], [data, extraPages]);

  return {
    feed,
    isLoadingFeed: isLoading,
    feedError: error,
    hasMore,
    isLoadingMore,
    loadMore,
  };
};
